import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";

import { environment } from "../../../environments/environment";

/** The shape of `GET /api/v1/portal/me`. */
export interface IPortalMe {

  sub: string;

  email: string;

  source: "auth0" | "entra";

  roles: string[];

  isDavacoAdmin: boolean;

  organizationId: string | null;

  actingOrgSlug: string | null;

  readOnly: boolean;
}

/**
 * Asks the API who the caller is.
 *
 * This is the AUTHORITY on roles, and the id token is not. The two disagree in
 * practice: an Entra access token issued for the API resource carries the app
 * roles assigned on that API's service principal, while the id token issued for
 * the SPA carries its own — so an employee can hold the admin role for every
 * request the API serves and still look role-less to the browser. Reading roles
 * from the token alone hid the entire admin surface from someone entitled to it.
 *
 * It stays a UI affordance either way: customer-portal-api re-decides from the
 * verified token on every request it serves.
 */
@Injectable({ providedIn: "root" })
export class MeService {

  private readonly http = inject(HttpClient);

  public async load(): Promise<IPortalMe | null> {
    try {
      return await firstValueFrom(
        this.http.get<IPortalMe>(`${environment.apiBaseUrl}/portal/me`)
      );
    } catch {
      // An unreachable or refusing /me must not stop the portal from rendering.
      // The guards then fall back to what the token claims, which is the
      // conservative answer.
      return null;
    }
  }
}
